/* ------------------------------------------------------------------ *
 * ------------------------------------------------------------------ */
import axios from "axios";
import { qs }   from "../utils/queryString";

/* ---------- constants -------------------------------------------- */
const GET_OMOP_START        = "IVE/GET_OMOP_START";
const GET_OMOP_SUCCESS      = "IVE/GET_OMOP_SUCCESS";
const GET_OMOP_ERROR        = "IVE/GET_OMOP_ERROR";

const UPDATE_OMOP_FILTERS   = "IVE/UPDATE_OMOP_FILTERS";
const UPDATE_OMOP_PAGE      = "IVE/UPDATE_OMOP_PAGE";
const UPDATE_OMOP_PAGE_SIZE = "IVE/UPDATE_OMOP_PAGE_SIZE";

const UPDATE_ALERT          = "UPDATE_ALERT";
const RESET_ALERT           = "RESET_ALERT";

/* ---------- helpers ---------------------------------------------- */
const makeTable = () => ({
  data:      [],
  count:     0,
  filters:   {},
  page:      1,
  pageSize:  25,
  loading:   false,
  error:     null,
});

/* ---------- initial state ---------------------------------------- */
const initialState = {
  alert:  null,
  tables: {
    person:      makeTable(),
    measurement: makeTable(),
    observation: makeTable(),
  },
};

/* ------------------------------------------------------------------ *
 * ------------------------------------------------------------------ */

/**
 * getOmop ― fetch rows (and count) for any OMOP table.
 * @param {string} table          person | measurement | observation…
 * @returns thunk
 */
export const getOmop = (table) => async (dispatch, getState) => {
  const slice    = getState().ive?.omop?.tables?.[table] ?? makeTable();
  if (slice.loading) return
  const { page, pageSize, filters } = slice;
  const hasFilters = Object.keys(filters).length > 0;

  const params   = { page, pageSize };
  const base     = `/api/omop/${table}`;
  const rowsUrl  = hasFilters
    ? `${base}/search?${qs(params)}`
    : `${base}?${qs(params)}`;
  const countUrl = hasFilters ? `${base}/search/count` : `${base}/count`;

  dispatch({ type: GET_OMOP_START, table });

  try {
    /* rows -------------------------------------------------------- */
    const rowsRes = hasFilters
      ? await axios.post(rowsUrl, filters)
      : await axios.get(rowsUrl);

    /* count (best-effort) ---------------------------------------- */
    let count = Array.isArray(rowsRes.data) ? rowsRes.data.length : 0;
    try {
      const cntRes = hasFilters
        ? await axios.post(countUrl, filters)
        : await axios.get(countUrl);
      const raw    = cntRes.data;
      count = typeof raw === "number"
        ? raw
        : Array.isArray(raw) ? raw.length : raw?.total ?? count;
    } catch (cntErr) {
      console.warn(`No count endpoint for ${table}`, cntErr.response?.status);
    }

    dispatch({
      type:    GET_OMOP_SUCCESS,
      table,
      payload: { data: rowsRes.data, count, page, pageSize, filters },
    });
  } catch (err) {
    console.error(err);
    dispatch({ type: GET_OMOP_ERROR, table, error: err.message });
    dispatch({
      type:  UPDATE_ALERT,
      alert: { message: err.message || "Fetch failed", severity: "warning" },
    });
    setTimeout(() => dispatch({ type: RESET_ALERT }), 3000);
  }
};

/* simple action creators (CADA names) ----------------------------- */
export const updateOmopFilters   = (table, filters) => ({
  type: UPDATE_OMOP_FILTERS,   table, filters
});
export const updateOmopPage      = (table, page)   => ({
  type: UPDATE_OMOP_PAGE,      table, page
});
export const updateOmopPageSize  = (table, size)   => ({
  type: UPDATE_OMOP_PAGE_SIZE, table, pageSize: size
});

/* ------------------------------------------------------------------ *
 *  Reducer                                                           *
 * ------------------------------------------------------------------ */
export default function omopReducer(state = initialState, action) {
  switch (action.type) {
    case GET_OMOP_START:
      return patch(state, action.table, { loading: true,  error: null });

    case GET_OMOP_SUCCESS:
      return patch(state, action.table, {
        ...action.payload,
        loading: false,
        error:   null,
      });

    case GET_OMOP_ERROR:
      return patch(state, action.table, {
        loading: false,
        error:   action.error,
      });

    case UPDATE_OMOP_FILTERS:
      return patch(state, action.table, { filters: action.filters, page: 1 });

    case UPDATE_OMOP_PAGE:
      return patch(state, action.table, { page: action.page });

    case UPDATE_OMOP_PAGE_SIZE:
      return patch(state, action.table, {
        pageSize: action.pageSize,
        page:     1,
      });

    /* global alerts (shared convention) -------------------------- */
    case UPDATE_ALERT:
      return { ...state, alert: action.alert };
    case RESET_ALERT:
      return { ...state, alert: null };

    default:
      return state;
  }
}

/* ---------- tiny helper ------------------------------------------ */
function patch(state, table, changes) {
  return {
    ...state,
    tables: {
      ...state.tables,
      [table]: { ...(state.tables[table] || makeTable()), ...changes },
    },
  };
}
